import React from 'react';
import { useTranslation } from 'react-i18next';
import { ActionFull } from 'interfaces/action.interface';
import { ChevronUpIcon, dayjs, RootState, ShortUser, UUID } from '@umanforge/common';
import DatePickerSoloInput from '@common_components/inputs/datePickerInputSolo.component';
import InputTime from '../../../components/inputs/selectTime.component';
import { Tooltip } from 'react-tooltip';
import { SectorSettings } from 'interfaces/setting.interface';
import { TaskFull } from 'interfaces/task.interface';
import useActionTile from '../hook/actionTile.hook';
import ImageOnError from 'components/other/ImageOnError';
import { Note } from 'interfaces/note.interface';
import NoteTile from './noteTile.component';
import { useSelector } from 'react-redux';


interface ActionTileProps {
  className?: string;
  onChange?: () => void;
  disable?: boolean;
  user: ShortUser;
  action: ActionFull | undefined;
  selected: boolean;
  setSelected: ( actionId: UUID | undefined ) => void;
  settings: SectorSettings | undefined;
  task: TaskFull;
  isAdmin: boolean;
}

const ActionTile: React.FC<ActionTileProps> = ( {
  className = 'w-full',
  disable = false,
  onChange,
  user,
  action,
  selected = true,
  setSelected,
  settings,
  task,
  isAdmin = false
} ) => {
  const { t } = useTranslation();

  const {
    dateStart,
    dateEnd,
    timeStart, setTimeStart,
    timeEnd, setTimeEnd,
    notes, setNotes, notesSave,
    images,
    cannotAchieve, setCannotAchieve,
    colorStatus,
    isFormValid,
    isFormChanged,
    errors,
    handleValidateAction, handleSaveAction,
    handleChangeDateStart, handleChangeDateEnd
  } = useActionTile( action, user, task, ( disable || !isAdmin ), onChange );


  const me = useSelector( ( state: RootState ) => {return state.user.value;} );

  return (
    <div className={`rounded-md ${className} p-3 ${ colorStatus }`} >
      <div className='relative w-full flex items-center'>
        <div className='flex gap-2 relative items-center w-4/12 mr-4'>
          <p className={'text-gray-600 truncate flex items-center'}>
            { user.fullname }
          </p>
        </div>
        <div className='w-full'>
          { action ? <>
            <p>{`${ dayjs( action.dateStart ).format( 'HH:mm' ) } - ${ action.dateEnd ? dayjs( action.dateEnd ).format( 'HH:mm' ) : '--:--' }`}</p>
            { action.images.map( img => {
              return <p key={action.id + '' + img.id}>{ img.originalName }</p>;
            } ) }
          </> : <>
            <p>{ t( 'translation:errors.no_action' )}</p>
          </>
          }
        </div>

        <div className='py-1'>
          <div
            id={`${user.id}_modify_button`}
            className={`h-10 px-3 w-fit flex items-center rounded-lg  ${ !disable ? ( action ? 'bg-blue-300 hover:bg-blue-300/80' : 'bg-green-300 hover:bg-green-300/80' ) : 'bg-gray-300'}`}
            onClick={ () => { setSelected( selected ? undefined : user.id ); } }
          >
            <label htmlFor={`${user.id}_modify_button`} className='w-fit px-2 text-center whitespace-nowrap'>
              { ( disable || !isAdmin ) ? t( 'translation:see_action' ) : action ? t( 'translation:modify_action' ) : t( 'translation:start_action' ) }
            </label>

            { <ChevronUpIcon className={`p-1 ${selected && 'rotate-180'}`} /> }
          </div>
        </div>
      </div>

      { selected &&
        <div className='w-full'>
          <div className='mt-5 mb-2'>
            <div className='flex flex-col gap-y-3'>
              <div className='flex gap-x-5'>
                <DatePickerSoloInput
                  label={ t( 'translation:beginning_of_action' ) }
                  defaultValue={ dateStart }
                  onChange={ handleChangeDateStart }
                  textSize={'w-3/4'}
                  calendarPosition={'bottom'}
                  disable={ disable || !isAdmin }
                />
                <div className='w-1/2'>
                  <InputTime
                    step={5}
                    min={ dayjs( timeStart ).set( 'hour', settings?.calendarStartHour?.value ?? 0 ).startOf( 'hour' )  }
                    max={ dayjs( timeStart ).set( 'hour', settings?.calendarEndHour?.value ?? 24 ).startOf( 'hour' )  }
                    value={ timeStart }
                    onChange={setTimeStart}
                    disable={ disable || !isAdmin }
                  />
                </div>
              </div>
              { errors.dateStart != null && <p className="text-red-500 h-6 -mt-3">{ errors.dateStart }</p> }

              <div className='flex gap-x-5'>
                <DatePickerSoloInput
                  label={ t( 'translation:end_of_action' ) }
                  defaultValue={ dateEnd }
                  onChange={ handleChangeDateEnd }
                  textSize={'w-3/4'}
                  calendarPosition={'bottom'}
                  disable={ disable || !isAdmin }
                />
                <div className='w-1/2'>
                  { timeEnd ?
                    <InputTime
                      step={5}
                      min={ dayjs( timeEnd ).set( 'hour', settings?.calendarStartHour?.value ?? 0 ).startOf( 'hour' )  }
                      max={ dayjs( timeEnd ).set( 'hour', settings?.calendarEndHour?.value ?? 24 ).startOf( 'hour' )  }
                      value={ timeEnd }
                      onChange={ setTimeEnd }
                      disable={ disable || !isAdmin }
                    /> :
                    ( disable || !isAdmin ) ? <></> :
                      <div
                        id={`${user.id}_complete_action_button`}
                        className={'h-10 w-fit px-5 place-self-center flex items-center rounded-lg bg-green-300 hover:bg-green-300/80'}
                        onClick={ handleValidateAction }
                      >
                        <label htmlFor={`${user.id}_complete_action_button`} className='w-full h-fit text-center justify-center'>
                          { t( 'translation:complete_action' ) }
                        </label>
                      </div>
                  }
                </div>
              </div>
            </div>


            <div className='flex w-full h-fit mt-4 ' id={'cannot_achieve_action'} >
              <p className={'text-gray-600 w-1/3 flex items-center left'}>{ t( 'translation:cannot_achieve_action' ) }</p>
              <div className='w-auto flex items-center'>
                <label className="w-fit left start cursor-pointer" onClick={ () => {
                  if ( !disable && isAdmin ) {
                    setCannotAchieve( !cannotAchieve );
                  }
                } }>
                  <input
                    type="checkbox"
                    value=""
                    className="sr-only peer"
                    name={'day_switch'}
                    checked={ cannotAchieve }
                    readOnly
                  />
                  <div className="relative w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all  peer-checked:bg-red-600"></div>
                </label>
                <Tooltip
                  anchorSelect={ '#cannot_achieve_action' }
                  className='z-[1000] max-w-[600px] text-wrap'
                  place="top-start"
                  noArrow
                  positionStrategy='fixed'
                >
                  <span>
                    <span className='text-[14px] font-semibold underline underline-offset-2'> { t( 'translation:cannot_achieve_action' ) } </span>
                    <p className='pl-6 pt-1'> { t( 'translation:there_is_a_problem_with_this_action_so_you_cannot_complete_it' ) } </p>
                  </span>
                </Tooltip>
              </div>
              <p className="h-6 -mt-1"/>
            </div>

            <div className='mt-5'/>
            <p className={'text-gray-600 truncate flex items-center'}>
              { t( 'translation:notes' ) }
            </p>
            <div className='flex gap-4 relative items-center w-full mb-4 rounded-lg border border-gray-200 p-2 bg-white/30'>
              <div className='flex flex-col gap-y-2 w-full'>
                { notes && notes.map( ( note, index ) => {
                  return <NoteTile
                    note={ note }
                    user={ me! }
                    isAdmin={ true }
                    key={`input_text_${index} ${ notes.map( n => { return n.id + '_' + n.content + '_' + ( n.disabled ? 'true' : 'false' ); } ).join( '-' )}`}
                    onDelete={ () => {
                      const res = [ ...notes ];
                      res.splice( index, 1 );
                      setNotes( res );
                    } }
                    onDisable={ () => { setNotes( [ ...notes ] ); }}
                    className={ ( note.disabled ? 'bg-red-100' : 'bg-white/50 hover:bg-white/80' )}
                    disable={ notesSave.some( n => { return n.id == note.id && n.disabled && n.disabled !== true; } ) || ( disable || !isAdmin ) /* Si il est deja disable en BDD, ne pas pouvoir le modifier. !== true car la valeur de BDD est un nombre */  }
                  />;
                } ) }

                { !( disable || !isAdmin ) &&
                  <div
                    id={`${user.id}_save_button`}
                    className={'h-10 w-36 flex items-center place-self-start rounded-lg bg-blue-300 hover:bg-blue-300/80'}
                    onClick={ () => { setNotes( [ ...notes, {
                      idUser: ( me! as ShortUser ).id,
                      authorName: ( me! as ShortUser ).fullname,
                      registerDate: dayjs().toISOString(),
                      idAction: action?.id
                    } as Note ] ); } }
                  >
                    <label htmlFor={`${user.id}_save_button`} className='w-full h-fit text-center justify-center'>
                      { t( 'common:add' ) }
                    </label>
                  </div>
                }
              </div>
            </div>

            {/* TODO images */}
            { images && images.length > 0 &&
              <>
                <div className='mt-5'/>
                <p className={'text-gray-600 truncate flex items-center'}>
                  { t( 'translation:images' ) }
                </p>
                <div className='flex gap-4 relative items-center w-full mb-4 rounded-lg border border-gray-200 p-2 bg-white/30'>
                  <div className='flex flex-col gap-y-2 w-full'>
                    { images.map( ( image, index ) => {
                      return <div key={`input_text_${index}`} className='w-full flex'>
                        <div className=' w-[30%] text-[14px] '>
                          <div className='flex w-full gap-x-3'>
                            <p className='text-gray-800'>{ t( 'common:creation_date' ) }:</p>
                            <p className='text-gray-600'>{ dayjs( image.uploadDate ).format( 'L HH:mm' ) }</p>
                          </div>
                          <div className='flex w-full gap-x-3'>
                            <p className='text-gray-800'>{ t( 'common:author' ) }:</p>
                            <p className='text-gray-600 text-ellipsis overflow-hidden h-6'> { image.originalName }</p>
                          </div>
                        </div>

                        <div className={'w-[70%] max-w-[70%] flex'}>
                          <ImageOnError src={ image.originalName }/>
                        </div>
                      </div>;
                    } ) }
                  </div>
                </div>
              </>
            }
          </div>


          { !( disable || !isAdmin ) &&
            <div className='p-1 w-full'>
              {/* <div
                id={`${user.id}_flashmeeting_button`}
                className={`h-10 w-36 flex items-center place-self-start rounded-lg  ${ ( true ) ? 'bg-blue-300 hover:bg-blue-300/80' : 'bg-gray-300'}`}
                onClick={ handleFlashmeetingAction }
              >
                <label htmlFor={`${user.id}_flashmeeting_button`} className='w-full h-fit text-center justify-center'>
                  { t( 'common:flashmeeting' ) }
                </label>
              </div>

              <div className='w-[80%]'/>
  */}
              <div
                id={`${user.id}_save_button`}
                className={`h-10 w-36 flex items-center place-self-end rounded-lg  ${ ( isFormValid && isFormChanged ) ? 'bg-green-300 hover:bg-green-300/80' : 'bg-gray-300'}`}
                onClick={ () => {
                  if ( isFormValid && isFormChanged ) {
                    handleSaveAction();
                  }
                } }
              >
                <label htmlFor={`${user.id}_save_button`} className='w-full h-fit text-center justify-center'>
                  { t( 'common:save' ) }
                </label>
              </div>
            </div>
          }

        </div>
      }
    </div>
  );
};

export default ActionTile;
